import { useEffect, useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import * as settingsApi from '@/api/settings'
import { useAuth } from '@/auth/AuthContext'
import { BrandMark } from '@/components/BrandMark'
import { useSiteSettings } from '@/siteSettingsContext'

function IconExpiries({ className = 'h-4 w-4' }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="1.75"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  )
}

/**
 * App chrome: optional full-height fixed left sidebar; main column scrolls.
 * Mirrors Bekat Dash layout (sidebar edge-to-edge, content scrolls independently).
 */
export function AppShell({ title, sidebar, children }) {
  const { user, logout, isAdmin, canUseAiSearch } = useAuth()
  const { siteTitle, logoText, appVersion } = useSiteSettings()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [expiryCount, setExpiryCount] = useState(null)
  const onAiSearch = location.pathname === '/ai-search'
  const onExpiries = location.pathname === '/expiries'

  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!isAdmin) {
      setExpiryCount(null)
      return undefined
    }
    let cancelled = false
    async function loadCount() {
      try {
        const data = await settingsApi.listExpiries()
        if (!cancelled) {
          setExpiryCount(
            typeof data.total_matched === 'number'
              ? data.total_matched
              : (data.items || []).length,
          )
        }
      } catch {
        if (!cancelled) setExpiryCount(null)
      }
    }
    loadCount()
    return () => {
      cancelled = true
    }
  }, [isAdmin, location.pathname])

  const brandBlock = (
    <div className="shrink-0 border-b border-sidebar-border px-4 py-4">
      <BrandMark
        siteTitle={siteTitle}
        logoText={logoText}
        appVersion={appVersion}
      />
    </div>
  )

  const sidebarPanel = sidebar ? (
    <>
      {brandBlock}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{sidebar}</div>
    </>
  ) : null

  const expiryBadge =
    expiryCount != null ? (
      <span
        className={[
          'min-w-[1.25rem] rounded px-1.5 py-0.5 text-center text-xs tabular-nums',
          expiryCount > 0
            ? 'bg-red-100 font-bold text-red-700'
            : 'bg-slate-100 font-semibold text-slate-500',
        ].join(' ')}
        aria-label={
          expiryCount === 1
            ? '1 item due soon'
            : `${expiryCount} items due soon`
        }
      >
        {expiryCount}
      </span>
    ) : null

  return (
    <div className="flex h-dvh overflow-hidden bg-gradient-to-br from-slate-100 via-sky-50 to-slate-200 text-brand">
      {sidebar ? (
        <aside className="hidden h-full w-64 shrink-0 flex-col bg-sidebar text-sidebar-foreground lg:flex">
          {sidebarPanel}
        </aside>
      ) : null}

      {sidebar && mobileOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 flex w-64 max-w-[85vw] flex-col bg-sidebar text-sidebar-foreground shadow-xl">
            {sidebarPanel}
          </aside>
        </div>
      ) : null}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex shrink-0 items-center justify-between gap-4 border-b border-slate-200/80 bg-white/80 px-4 py-3 backdrop-blur sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            {sidebar ? (
              <button
                type="button"
                className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm font-medium hover:bg-white lg:hidden"
                onClick={() => setMobileOpen(true)}
              >
                Menu
              </button>
            ) : (
              <BrandMark
                siteTitle={siteTitle}
                logoText={logoText}
                appVersion={appVersion}
                tone="light"
                compact
              />
            )}
            {title ? (
              <span className="truncate text-sm font-semibold text-brand-muted lg:hidden">
                {title}
              </span>
            ) : null}
            {isAdmin ? (
              <Link
                to="/customers"
                className={[
                  'inline-flex rounded-md border px-3 py-1.5 text-sm font-semibold transition-colors',
                  location.pathname === '/customers'
                    ? 'border-slate-400 bg-slate-100 text-brand'
                    : 'border-slate-300 bg-white text-brand hover:bg-slate-50',
                ].join(' ')}
              >
                All customers
              </Link>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-3 text-sm">
            {isAdmin ? (
              <Link
                to="/expiries"
                className={[
                  'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-semibold transition-colors',
                  onExpiries
                    ? 'border-red-200 bg-red-50 text-red-800'
                    : 'border-slate-300 bg-white text-brand hover:bg-slate-50',
                ].join(' ')}
              >
                <IconExpiries
                  className={[
                    'h-4 w-4 shrink-0',
                    expiryCount > 0 ? 'text-red-600' : 'text-brand-muted',
                  ].join(' ')}
                />
                <span>ToDo / Expiring</span>
                {expiryBadge}
              </Link>
            ) : null}
            {canUseAiSearch && !onAiSearch ? (
              <Link
                to="/ai-search"
                className="rounded-md bg-brand px-3 py-1.5 text-sm font-semibold text-white hover:bg-slate-800"
              >
                AI Search
              </Link>
            ) : null}
            <span className="hidden text-brand-muted sm:inline">{user?.email}</span>
            <button
              type="button"
              onClick={() => logout()}
              className="rounded-md border border-slate-300 px-3 py-1.5 font-medium hover:bg-white"
            >
              Sign out
            </button>
          </div>
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-6 sm:py-8">
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  )
}
